/**
 * @fileoverview Mock backend for inviting users and managing invitations.
 * @description Registers GET /identity/configuration, GET /authorization/me/assignable-roles,
 * GET /users/eligible-reporting-managers, GET /users/eligible-departments, GET/POST
 * /identity/invitations and POST
 * /identity/invitations/{id}/resend and /revoke on this feature's mock adapter, answering in the
 * backend's envelopes (with `meta` on the paginated lists) and with its error codes, so the screens
 * can be used without a backend. The mock doesn't narrow anything by the signed-in admin's own
 * authority the way the real backend does. A no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { fail, numberParam, ok, queryParams } from './mock-response.utils'
import { userInvitationsMockAdapter } from './user-invitations-mock-adapter'
import {
  createInvitation,
  findInvitation,
  hasPendingInvitation,
  isManageable,
  listEligibleManagers,
  listInvitations,
  MAX_RESENDS,
  MOCK_ASSIGNABLE_ROLES,
  MOCK_SALES_DEPARTMENT,
  RESEND_COOLDOWN_MS,
  resend,
  revoke,
} from './user-invitations-mock-store'

import type {
  IdentityConfiguration,
  InvitationStatus,
  SendInvitationRequest,
  SendInvitationResponse,
} from '@/features/user-invitations/types/user-invitations.types'
import type { AxiosRequestConfig } from 'axios'

const REVOKE_REASONS = [
  'USER_CANCELLED',
  'WRONG_EMAIL',
  'ROLE_CHANGED',
  'TENANT_CHANGED',
  'SECURITY_POLICY',
]

const CONFIGURATION: IdentityConfiguration = {
  invitationStatuses: ['PENDING', 'ACCEPTED', 'EXPIRED', 'REVOKED', 'SUPERSEDED'].map((code) => ({
    code,
    labelKey: `identity.invitationStatus.${code}`,
  })),
  userStatuses: ['INVITED', 'REGISTERED', 'ACTIVE', 'INACTIVE', 'LOCKED', 'SUSPENDED'].map(
    (code) => ({ code, labelKey: `identity.userStatus.${code}` }),
  ),
  invitationRevokeReasons: REVOKE_REASONS.map((code) => ({
    code,
    labelKey: `identity.invitationRevokeReason.${code}`,
  })),
  otp: { length: 6, format: 'NUMERIC_STRING', channel: 'EMAIL' },
  invitation: { defaultValiditySeconds: 48 * 60 * 60 },
}

/**
 * @description Pulls the invitation id out of a /identity/invitations/{id}/{action} URL.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {string} The invitation id.
 */
function invitationIdFrom(config: AxiosRequestConfig): string {
  return config.url?.split('/').at(-2) ?? ''
}

if (userInvitationsMockAdapter) {
  const adapter = userInvitationsMockAdapter

  adapter.onGet('/identity/configuration').reply(() => [200, ok(CONFIGURATION)])

  adapter
    .onGet('/authorization/me/assignable-roles')
    .reply(() => [
      200,
      ok(MOCK_ASSIGNABLE_ROLES, { page: 1, limit: 100, total: MOCK_ASSIGNABLE_ROLES.length }),
    ])

  adapter.onGet('/users/eligible-reporting-managers').reply((config) => {
    const roleId = String((config.params as Record<string, unknown> | undefined)?.targetRoleId)
    const managers = listEligibleManagers(roleId)
    return [200, ok(managers, { page: 1, limit: 100, total: managers.length })]
  })

  // Every role this mock can invite is placed in the one Sales department: fixed for a Sales role.
  adapter.onGet('/users/eligible-departments').reply((config) => {
    const roleIds = (queryParams(config).roleIds ?? '').split(',').filter(Boolean)
    const roles = MOCK_ASSIGNABLE_ROLES.filter((role) => roleIds.includes(role.roleId))
    if (roles.length === 0) {
      return fail(403, '2008', 'Forbidden')
    }
    const { departmentId, departmentCode, name } = MOCK_SALES_DEPARTMENT
    const department = { departmentId, departmentCode, name }
    const isSales = roles.every((role) => role.roleCode.startsWith('SALES_'))
    return [
      200,
      ok(
        {
          items: [{ ...department, applicableRoleIds: roleIds }],
          fixedDepartmentId: isSales ? department.departmentId : null,
          defaultDepartmentId: department.departmentId,
          selectionMode: isSales ? 'FIXED' : 'SELECTABLE',
        },
        { page: 1, limit: 100, total: 1 },
      ),
    ]
  })

  adapter.onGet('/identity/invitations').reply((config) => {
    const params = (config.params ?? {}) as { status?: InvitationStatus; search?: string }
    const page = numberParam(config, 'page', 1)
    const limit = numberParam(config, 'limit', 20)
    const all = listInvitations(params.status, params.search)
    const items = all.slice((page - 1) * limit, page * limit)
    return [200, ok(items, { page, limit, total: all.length })]
  })

  adapter.onPost('/identity/invitations').reply((config) => {
    const request = JSON.parse(config.data as string) as SendInvitationRequest

    if (request.departmentId !== MOCK_SALES_DEPARTMENT.departmentId) {
      return fail(422, '3015', 'Department is not eligible')
    }

    if (hasPendingInvitation(request.email)) {
      return fail(409, '1054', 'User cannot be invited in the current state.')
    }

    const created = createInvitation(request)
    const response: SendInvitationResponse = {
      userId: created.userId,
      invitationId: created.invitationId,
      invitationStatus: 'PENDING',
      deliveryStatus: 'QUEUED',
      expiresAt: created.expiresAt,
    }
    return [200, ok(response)]
  })

  adapter.onPost(/\/identity\/invitations\/[^/]+\/resend$/).reply((config) => {
    const record = findInvitation(invitationIdFrom(config))

    if (!record) {
      return fail(404, '1057', 'Invitation not found.')
    }
    if (!isManageable(record)) {
      return fail(409, '1058', 'Invitation state conflict.')
    }
    if (record.resendCount >= MAX_RESENDS) {
      return fail(403, '1060', 'Invitation resend limit reached.')
    }
    if (Date.now() - record.lastSentAt < RESEND_COOLDOWN_MS) {
      return fail(429, '1059', 'Invitation resend cooldown is active.')
    }

    const updated = resend(record)
    return [
      200,
      ok({
        invitationId: updated.invitationId,
        invitationStatus: 'PENDING',
        deliveryStatus: 'QUEUED',
        expiresAt: updated.expiresAt,
      }),
    ]
  })

  adapter.onPost(/\/identity\/invitations\/[^/]+\/revoke$/).reply((config) => {
    const record = findInvitation(invitationIdFrom(config))
    const { reason } = JSON.parse(config.data as string) as { reason?: string }

    if (!reason || !REVOKE_REASONS.includes(reason)) {
      return fail(400, '0001', 'Validation failed.')
    }
    if (!record) {
      return fail(404, '1057', 'Invitation not found.')
    }
    if (record.status !== 'REVOKED' && !isManageable(record)) {
      return fail(409, '1058', 'Invitation state conflict.')
    }

    revoke(record)
    return [200, ok({ invitationId: record.invitationId, revoked: true })]
  })
}
